import { useEffect, useMemo, useState } from 'react';
import { s, authHeaders } from '../listPageStyles';
import { MultiChipSelect } from './MultiChipSelect';
import { csvToArr, arrToCsv } from './campaignUtils';

function Collapse({ title, icon, defaultOpen, children }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="cmp-collapse">
      <button type="button" className="cmp-collapse-head" onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true">{icon}</span>{title}
        <span style={{ marginLeft: 'auto' }} aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="cmp-collapse-body">{children}</div>}
    </div>
  );
}

function LimitField({ label, value, onChange, isEdit, suffix = '', placeholder = 'Sınırsız' }) {
  return (
    <div style={s.formGroup}>
      <label style={s.label}>{label}<span className="cmp-opt-tag">Opsiyonel</span></label>
      <input
        style={isEdit ? s.input : s.inputView}
        type="number" min="0" step={suffix === '₺' ? '0.01' : '1'}
        value={value === 0 || value === '0' ? '' : value}
        readOnly={!isEdit}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === '' ? 0 : e.target.value)}
      />
    </div>
  );
}

export function CampaignRestrictionsTab({ form, setForm, isEdit }) {
  const set = (patch) => setForm((c) => ({ ...c, ...patch }));

  // Çakışan promosyonlar için diğer kampanyalar.
  const [campaigns, setCampaigns] = useState([]);
  useEffect(() => {
    let alive = true;
    fetch('/api/campaigns?take=500', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        const data = (p && (p.Data || p.data)) || p || {};
        const items = data.items || data.Items || [];
        if (alive) setCampaigns(Array.isArray(items) ? items : []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const campaignOpts = useMemo(() => campaigns.map((c) => ({
    value: c.id || c.Id,
    label: c.name || c.Name || c.code || c.Code,
  })), [campaigns]);

  const conflictItems = useMemo(() => {
    const ids = csvToArr(form.conflictingCampaignIdsCsv);
    return ids.map((id) => campaignOpts.find((o) => o.value === id) || { value: id, label: id });
  }, [form.conflictingCampaignIdsCsv, campaignOpts]);

  const searchCampaigns = async (q) => {
    const term = String(q || '').toLocaleLowerCase('tr-TR');
    const selfId = form.code; // exclude not strictly needed; filter by label
    return campaignOpts
      .filter((o) => o.value !== selfId && (!term || o.label.toLocaleLowerCase('tr-TR').includes(term)))
      .slice(0, 50);
  };

  const setConflicts = (next) => set({ conflictingCampaignIdsCsv: arrToCsv(next.map((x) => x.value)) });

  return (
    <div>
      <h3 className="cmp-section-title">⚙ Promosyon Kısıtlamaları</h3>
      <p className="cmp-section-sub">Promosyonun kullanım limitlerini ve kurallarını tanımlayın.</p>

      <Collapse title="Kullanım Limitleri" icon="⚙" defaultOpen>
        <div style={s.formRow}>
          <LimitField label="Toplam Kullanım Limiti" value={form.maxApplications} onChange={(v) => set({ maxApplications: v })} isEdit={isEdit} />
          <LimitField label="Müşteri Başına Limit" value={form.maxPerCustomer} onChange={(v) => set({ maxPerCustomer: v })} isEdit={isEdit} />
        </div>
        <div style={s.formRow}>
          <LimitField label="Distribütör Başına Limit" value={form.maxPerDistributor} onChange={(v) => set({ maxPerDistributor: v })} isEdit={isEdit} />
          <LimitField label="Günlük Kullanım Limiti" value={form.maxPerDay} onChange={(v) => set({ maxPerDay: v })} isEdit={isEdit} />
        </div>
      </Collapse>

      <Collapse title="Bütçe Limitleri" icon="💰">
        <div style={s.formRow}>
          <LimitField label="Toplam Bütçe (₺)" value={form.budgetLimit} onChange={(v) => set({ budgetLimit: v })} isEdit={isEdit} suffix="₺" />
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary,#6b7280)', margin: 0 }}>
          Bütçe dolduğunda promosyon otomatik durur (kullanım verisi toplanmaya başladığında).
        </p>
      </Collapse>

      <Collapse title="Kombinasyonlar" icon="⚙">
        <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: isEdit ? 'pointer' : 'default' }}>
          <input type="checkbox" checked={!!form.isCombinable} disabled={!isEdit} onChange={(e) => set({ isCombinable: e.target.checked })} />
          Diğer promosyonlarla birlikte kullanılabilir
        </label>
        <p style={{ fontSize: 12, color: 'var(--text-secondary,#6b7280)', margin: '0 0 8px' }}>
          İşaretli değilse, bu promosyon tek başına uygulanır.
        </p>
        <div style={s.formGroup}>
          <label style={s.label}>Öncelik Seviyesi (0-100)</label>
          <input style={isEdit ? s.input : s.inputView} type="number" min="0" max="100" value={form.priority} readOnly={!isEdit} onChange={(e) => set({ priority: e.target.value })} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary,#6b7280)' }}>Yüksek öncelikli promosyonlar önce uygulanır.</span>
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>Çakışan Promosyonlar<span className="cmp-opt-tag">Opsiyonel</span></label>
          <MultiChipSelect items={conflictItems} onChange={setConflicts} fetchOptions={searchCampaigns} placeholder="Birlikte uygulanmayacak promosyon ara…" disabled={!isEdit} />
        </div>
      </Collapse>
    </div>
  );
}

export default CampaignRestrictionsTab;
